import { css } from '@emotion/css';
import {
  m,
  mobileMediaQuery,
  smallMobileMediaQuery,
  twoColumnCollapseMediaQuery,
} from '@snapchat/snap-design-system-marketing';

// Logo horizontal position (also used for background object-position)
const logoHorizontalPosition = '34.5%';

export const blockWrapperCss = css`
  .sdsm-block > div {
    padding-top: 0;
    padding-bottom: 0;
    max-width: none;
  }

  /*
  Since this is custom component
  Page does not pass skipBreakTemplate, 2 breaks are rendered
  these styles are to prevent break from appearing
  */
  + .sdsm-break-top-straight {
    display: none;
  }

  + .sdsm-break-top-straight + .sdsm-break-bottom-straight {
    display: none;
  }

  /* prevent block expanding when toggling the checkbox */
  .sdsm-block {
    background-position-y: center;
    background-size: cover;
    /** accomodate box shadows */
    overflow: visible;
  }

  ${twoColumnCollapseMediaQuery} {
    /* Center the block children container */
    section[data-testid='sdsm-block-children'] {
      margin: 0 auto;
    }
  }
`;

export const contentCss = css`
  padding: 26px;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${m('--spacing-l')};
  margin-left: 50%;
  width: 50%;
  position: relative;
  z-index: 1;

  ${twoColumnCollapseMediaQuery} {
    align-items: center;
    margin-left: 0;
    width: 100%;
    padding-top: 300px;
  }
`;

export const contentSpecificVersionCss = css`
  width: 100%;
  align-items: flex-start;
  max-width: 600px;
  margin-left: 0;
  margin-right: 0;
  gap: ${m('--spacing-s')};

  /* Override responsive behavior for specific version pages */
  ${twoColumnCollapseMediaQuery} {
    align-items: flex-start;
    margin-left: 0;
    width: 100%;
    padding-top: ${m('--spacing-l')};
  }
`;

export const contentHeightBlockCss = css`
  padding-top: ${m('--spacing-xxl')};
  padding-bottom: ${m('--spacing-xxl')};
  min-height: 680px;

  ${mobileMediaQuery} {
    min-height: min(680px, 80vh);
  }
`;

export const contentHeightSpecificVersionCss = css`
  padding-top: ${m('--spacing-l')};
  padding-bottom: ${m('--spacing-l')};
  min-height: auto;

  ${mobileMediaQuery} {
    min-height: auto;
  }
`;

export const logoCss = css`
  position: absolute;
  left: ${logoHorizontalPosition};
  top: 50%;
  transform: translate(-50%, -50%);
  height: 220px;
  width: 220px;
  z-index: 1;

  ${twoColumnCollapseMediaQuery} {
    left: 50%;
    top: 175px;
  }
`;

export const headerCss = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const headingCss = css`
  /* Override the default text-transform from h1/h2 styles */

  && {
    text-transform: none;
  }

  ${twoColumnCollapseMediaQuery} {
    text-align: center;
  }
`;

export const alignLeftOnTwoColumnCollapseCss = css`
  ${twoColumnCollapseMediaQuery} {
    text-align: left;
  }
`;

export const titleSpecificVersionCss = css`
  && {
    font-size: ${m('--h2-desktop-font-size')};
  }
`;

export const subtitleSpecificVersionCss = css`
  && {
    font-size: ${m('--h4-desktop-font-size')};
  }
`;

export const gradientTextCss = css`
  background: linear-gradient(270deg, #fff0b9 0%, #ffcff4 100%);
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export const subtitleWrapperCss = css`
  display: flex;
  gap: 30px;
  align-items: center;

  ${twoColumnCollapseMediaQuery} {
    justify-content: center;
  }
`;

export const subtitle2Css = css`
  opacity: 0.7;
`;

export const formCss = css`
  align-items: flex-start;
  margin: 0;
`;

export const formRowCss = css`
  display: flex;
  align-items: center;
  gap: ${m('--spacing-m')};

  ${twoColumnCollapseMediaQuery} {
    justify-content: center;
  }

  ${smallMobileMediaQuery} {
    flex-direction: column;
  }
`;

export const formRowSpecificVersionCss = css`
  ${twoColumnCollapseMediaQuery} {
    justify-content: flex-start;
  }
`;

export const formRowPlatformCss = css`
  .sdsm-form-field {
    margin: 0;
    flex-basis: auto;
    flex-grow: 0;
    width: auto;
  }

  select {
    padding-right: 80px;
    cursor: pointer;
    font-size: 16px;
    padding-top: 0;
    padding-bottom: 0;
    height: 40px;
    border-radius: 500px;
    background: ${m('--form-input-bg-color')};

    /*
    expanding orginal select styles
    packages/snap-design-system-marketing/src/components/Form/styles.ts#L127
    */
    &:-ms-expand {
      display: none;
    }
  }
`;

export const formFieldsCss = css`
  display: flex;
  flex-direction: column;
  gap: ${m('--spacing-m')};
`;

export const downloadButtonCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

export const selectHeightLargeCss = css`
  select {
    height: 48px;
  }
`;

export const gapLargeCss = css`
  gap: ${m('--spacing-l')};
`;

export const hiddenCss = css`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

export const alignRightCss = css`
  align-items: flex-end;
`;

export const alignCenterCss = css`
  align-items: center;
`;

export const rowAlignRightCss = css`
  justify-content: flex-end;
`;

export const rowAlignCenterCss = css`
  justify-content: center;
`;

export const contentWithBgImageCss = css`
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

export const blockContainerCss = css`
  min-height: 680px;
  overflow: visible;

  ${mobileMediaQuery} {
    min-height: min(680px, 80vh);
  }
`;

export const blockContainerSpecificVersionCss = css`
  min-height: auto;

  ${mobileMediaQuery} {
    min-height: auto;
  }
`;

export const backgroundImageContainerCss = css`
  position: absolute;
  inset: 0;
  overflow: hidden;
`;

export const backgroundImageCss = css`
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: ${logoHorizontalPosition} center;

  ${twoColumnCollapseMediaQuery} {
    object-position: center center;
  }
`;
